import { Button, DatePicker, Input, Select, Space, Table, Tabs, Tag, Typography } from "@arco-design/web-react";
import { useEffect, useState } from "react";
import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { describeLegacySyncResult, legacySyncBusinessUnit } from "../service/legacySyncService";
import type { LegacySyncEndpointPrefix } from "../types";
import { useLegacySyncRuntime } from "../runtime/useLegacySyncRuntime";

const groups = [
  { key: "point", title: "考察点", description: "只新增新库没有的考察点，已有考察点直接跳过。", targets: ["resource_point_spu"] },
  { key: "itinerary", title: "行程", description: "缺失行程整体新增主表、每日和节点；已有行程整组跳过，不补明细。", targets: ["itinerary"] },
  { key: "supplier", title: "供应商", description: "选择供应商子类型分别预览和同步，每个子类型独立记录时间。请先同步主体，再处理附属资料。", targets: [
    "dim_hous_level", "supplier_ground", "supplier_hotel", "resource_stay_fulfill_spu", "resource_stay_media",
    "supplier_bus", "supplier_guide", "supplier_contact", "supplier_bank_account",
    "supplier_quote_capability", "supplier_attachment",
  ] },
  { key: "handover", title: "合同交接", description: "项目下没有订单和名单时整体新增；已有任一主记录就整组跳过。", targets: ["order_handover"] },
];

const panelStyle = { padding: 20, border: "1px solid var(--color-border-2)", borderRadius: 8, background: "var(--color-bg-2)" };

/** 来源时间均为北京时间；展示到秒，请求保留原游标的微秒。 */
function displayTime(value?: string | null) {
  return value ? value.replace("T", " ").slice(0, 19) : "尚无成功记录";
}

type LegacySyncFeaturePageProps = {
  endpointPrefix?: LegacySyncEndpointPrefix;
  title?: string;
  description?: string;
  notice?: string;
};

/** 四类旧数据按开始时间至预览时当前时间查缺，人工确认后执行。 */
export function LegacySyncFeaturePage({
  endpointPrefix,
  title = "旧系统数据同步",
  description = "按时间范围补充旧有新无的数据。新库已有对象直接跳过，内容差异留待后续治理。",
  notice,
}: LegacySyncFeaturePageProps) {
  const runtime = useLegacySyncRuntime(endpointPrefix);
  const canExecute = canUsePermission(useCurrentAuthUser().data, "data.legacy_sync:execute");
  const [targetKey, setTargetKey] = useState("resource_point_spu");
  const [starts, setStarts] = useState<Record<string, string>>({});
  const [restoreId, setRestoreId] = useState("");
  const group = groups.find((item) => item.targets.includes(targetKey)) ?? groups[0];
  const target = runtime.targets.find((item) => item.targetKey === targetKey);
  const result = runtime.latestResult;
  const business = result?.businessResult;
  const unit = legacySyncBusinessUnit(result?.targetKey ?? targetKey);
  const busy = Boolean(runtime.previewingTargetKey || runtime.executingTargetKey || runtime.refreshingResult);
  const from = starts[targetKey] ?? target?.lastSuccessSourceUpdatedAt ?? "";
  const matched = result?.targetKey === targetKey && result.syncMode === "incremental" && Boolean(result.cursorFrom && result.cursorTo);
  const executable = matched && result?.planId && !result.hasMore && result.summary.plannedUpdate === 0
    && ["planned", "paused", "partial_failed"].includes(result.status);

  useEffect(() => {
    if (result) setTargetKey(result.targetKey);
    if (result?.cursorFrom && result.status !== "completed") {
      setStarts((values) => ({ ...values, [result.targetKey]: result.cursorFrom! }));
    }
    if (result?.status === "completed" && result.cursorTo) {
      setStarts((values) => { const next = { ...values }; delete next[result.targetKey]; return next; });
    }
  }, [result]);

  /** 切换对象时恢复该对象的结果，防止跨类别执行计划。 */
  function selectTarget(key: string) {
    setTargetKey(key);
    runtime.selectTargets([key]);
  }

  return (
    <main style={{ display: "grid", gap: 16, paddingBottom: 20 }}>
      <header>
        <Typography.Title heading={5}>{title}</Typography.Title>
        <Typography.Text type="secondary">{description}</Typography.Text>
        {notice && <div><Typography.Text type="warning">{notice}</Typography.Text></div>}
      </header>

      <section style={panelStyle}>
        <Tabs activeTab={group.key} onChange={(key) => selectTarget(groups.find((item) => item.key === key)!.targets[0])}>
          {groups.map((item) => <Tabs.TabPane key={item.key} title={item.title} disabled={busy} />)}
        </Tabs>
        <div style={{ display: "grid", gap: 18, paddingTop: 8 }}>
          <Typography.Text type="secondary">{group.description}</Typography.Text>
          {targetKey === "supplier_hotel" && <Typography.Text type="secondary">
            按酒店主档和经营证件的最近更新时间查缺，只新增酒店供应商主体。完成后请选择“供应商附件”重新预览、同步证件。
          </Typography.Text>}
          {group.key === "supplier" && (
            <Space wrap>
              <label htmlFor="legacy-sync-supplier">同步类型</label>
              <Select id="legacy-sync-supplier" aria-label="供应商同步类型" value={targetKey} disabled={busy} style={{ width: 280 }} onChange={selectTarget}
                options={group.targets.flatMap((key) => {
                  const item = runtime.targets.find((record) => record.targetKey === key);
                  return item ? [{ value: key, label: item.targetName }] : [];
                })} />
            </Space>
          )}
          {runtime.loading ? <span>正在加载同步信息...</span> : target ? <>
            <Space wrap>
              <Tag color="arcoblue">{target.sourceDatabaseName ?? "旧系统"} → {target.targetDatabaseName ?? target.databaseName}</Tag>
              <Typography.Text type="secondary">上次成功扫描至：{displayTime(target.lastSuccessSourceUpdatedAt)}（北京时间）</Typography.Text>
            </Space>
            <Space wrap size={12} align="center">
              <label htmlFor="legacy-sync-start">开始时间</label>
              <DatePicker id="legacy-sync-start" inputProps={{ "aria-label": "开始时间" }} showTime format="YYYY-MM-DD HH:mm:ss"
                placeholder="请选择开始时间" value={from ? displayTime(from) : undefined} disabled={busy}
                style={{ width: 250 }} onChange={(value) => {
                  setStarts((values) => ({ ...values, [targetKey]: value ? `${value.replace(" ", "T")}+08:00` : "" }));
                  runtime.invalidatePreview(targetKey);
                }} />
              <Typography.Text>至 当前时间</Typography.Text>
            </Space>
            <Typography.Text type="secondary">
              {target.supportsPreview
                ? "按旧库新增／修改时间核对，结束时间以本次预览为准。执行成功后，下次默认从该结束时间开始。"
                : target.nextStage}
            </Typography.Text>
            <Space wrap>
              <Button type="outline" loading={runtime.previewingTargetKey === targetKey} disabled={busy || !from || !target.supportsPreview}
                onClick={() => void runtime.handlePreview(targetKey, "incremental", { sourceUpdatedFrom: from })}>预览</Button>
              <Button type="primary" loading={Boolean(runtime.executingTargetKey)} disabled={busy || !canExecute || !target.supportsExecute || !executable}
                onClick={() => void runtime.handleExecute(targetKey, "incremental")}>
                {result?.status === "paused" || result?.status === "partial_failed" ? "继续同步" : "执行同步"}
              </Button>
              {result?.status === "started" && <Button disabled={!canExecute} onClick={() => void runtime.handlePause()}>暂停并安全收尾</Button>}
            </Space>
          </> : <Typography.Text type="secondary">暂无同步对象。</Typography.Text>}
        </div>
      </section>

      {runtime.errorMessage && <div role="alert" style={{ ...panelStyle, color: "rgb(var(--danger-6))" }}>{runtime.errorMessage}</div>}
      {runtime.previewingTargetKey && <div role="status" style={panelStyle}>
        {runtime.slowPreviewTargetKey ? "正在核对整个时间范围，耗时较长，请稍候。" : "正在预览时间范围内的数据..."}
      </div>}
      {result && <section role="status" style={{ ...panelStyle, display: "grid", gap: 12 }}>
        <Typography.Title heading={6} style={{ margin: 0 }}>{describeLegacySyncResult(result)}</Typography.Title>
        {result.cursorFrom && result.cursorTo && <span>本次范围：{displayTime(result.cursorFrom)} 至 {displayTime(result.cursorTo)}（北京时间）</span>}
        {business ? <>
          <Typography.Text type="secondary">
            本次范围共 {business.total} {unit}。{result.targetKey === "order_handover" ? "按定制号统计，每个定制单为一组。" : "按完整业务对象统计。"}不累加名单、收费、每日、节点等明细行数。
          </Typography.Text>
          <Space wrap size={32}>
            <div aria-label={result.mode === "dry_run" ? "可新增" : "已新增成功"}>
              <div>{result.mode === "dry_run" ? "可新增" : "已新增成功"}</div>
              <strong style={{ fontSize: 28, color: "rgb(var(--success-6))" }}>{result.mode === "dry_run" ? business.ready : business.synced}</strong> {unit}
            </div>
            <div aria-label="已有无需同步"><div>已有无需同步</div><strong style={{ fontSize: 28 }}>{business.existing}</strong> {unit}</div>
            <div aria-label={result.mode === "dry_run" ? "暂不能同步" : "仍未同步"}>
              <div>{result.mode === "dry_run" ? "暂不能同步" : "仍未同步"}</div>
              <strong style={{ fontSize: 28, color: "rgb(var(--warning-6))" }}>{result.mode === "dry_run" ? business.failed : business.pending}</strong> {unit}
            </div>
          </Space>
          <Typography.Text type="secondary">
            {result.mode === "dry_run" ? "点击执行同步后才会新增；已有对象保留新系统内容。" : "成功数量按本计划累计，包含继续同步前的成功结果；已有对象不计入新增成功。"}
          </Typography.Text>
          {business.pendingItems.some((item) => item.needsPreview) && <Typography.Text type="warning">
            来源数据问题需修正后重新预览；直接继续同步仍会使用原预览内容。
          </Typography.Text>}
          {result.mode === "execute" && result.status !== "started" && business.pending > 0 && <Typography.Text type="warning">
            成功的对象已保留；本次范围尚未全部完成，开始时间未推进。
          </Typography.Text>}
          {!!business.pendingItems.length && <>
            <Typography.Text bold>{result.mode === "dry_run" ? "暂不能同步的对象及原因" : "未同步的对象及原因"}</Typography.Text>
            <Table rowKey="legacyId" size="small" data={business.pendingItems}
              pagination={{ pageSize: 10, showTotal: true }} scroll={{ x: 620 }}
              columns={[
                { title: result.targetKey === "order_handover" ? "定制号" : "旧系统编号", dataIndex: "legacyId", width: 190 },
                { title: "名称／团号", dataIndex: "sourceName", width: 180, render: (name: string | null) => name || "—" },
                { title: "未同步原因与处理方式", dataIndex: "reason" },
              ]} />
          </>}
        </> : <>
          <Typography.Text type="warning">暂无法确认业务对象数量，请查看下方说明。</Typography.Text>
          <span>{result.message}</span>
        </>}
        {result.runId && <Button style={{ width: "fit-content" }} loading={runtime.refreshingResult} onClick={() => void runtime.refreshLatestResult()}>刷新结果</Button>}
        {!business && !!result.reviewItems?.length && <details open>
          <summary>待处理明细（{result.reviewItems.length}）</summary>
          <ul style={{ maxHeight: 260, overflow: "auto", paddingLeft: 24 }}>
            {result.reviewItems.map((item, index) => <li key={`${item.legacyId}:${index}`}>
              {item.sourceName ?? item.legacyId ?? "来源记录"}：{item.reason}
            </li>)}
          </ul>
        </details>}
        <details>
          <summary>运行详情</summary>
          <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
            {business && <span>{result.message}</span>}
            <span>同步对象：{target?.targetName ?? result.targetKey}</span>
            <span>计划编号：{result.planId ?? "未生成"}</span>
            <span>运行编号：{result.runId ?? "尚未执行"}</span>
            <span>目标表：{target?.databaseName}.{target?.targetTable}</span>
          </div>
        </details>
      </section>}
      <details style={{ color: "var(--color-text-3)" }}>
        <summary>恢复查看已有运行</summary>
        <Space wrap style={{ marginTop: 12 }}>
          <Input aria-label="恢复运行编号" placeholder="输入运行编号" value={restoreId} disabled={busy} onChange={setRestoreId} style={{ width: 320 }} />
          <Button disabled={busy || !restoreId.trim()} onClick={() => void runtime.restoreRun(restoreId.trim())}>恢复查看</Button>
        </Space>
      </details>
    </main>
  );
}
